import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from '../../analytics';
import SingleFreeQuestionView from './SingleFreeQuestionView';
import PlansModal from '../../components/PlansModal';
import DashboardTopbar from '../../components/DashboardTopbar';
import Dropdown from '../../components/Dropdown';
import { useCourse } from '../../contexts/CourseContext';
import { FREE_QUESTIONS_LIMIT } from '../../planLimits';
import './FreeQuestions.css';

const apiUrl = process.env.REACT_APP_API_URL;

const PAGE_SIZE = 20;

function FreeQuestions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeCourseId } = useCourse();
  const { subjectId: initialSubjectId } = location.state || {};
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(initialSubjectId ? String(initialSubjectId) : '');
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [hasPaidPlan1, setHasPaidPlan1] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showAnswer, setShowAnswer] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const awnsers = ["A", "B", "C", "D", "E"];

  const getQuestionImageSrc = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:')) return imageUrl;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) return imageUrl;
    return `${apiUrl}uploads/${imageUrl}`;
  };

  const fetchQuestions = useCallback(async (subjectId, topicId) => {
    setLoadingQuestions(true);
    try {
      const body = subjectId ? { subjectId: Number(subjectId) } : {};
      if (topicId) body[activeCourseId ? 'topicId' : 'catalogTopicId'] = Number(topicId);
      body.page = 1;
      body.pageSize = PAGE_SIZE;
      body.random = true;
      body.courseId = activeCourseId || undefined;
      const response = await axios.post(apiUrl + "freeQuestions/", body);
      const data = response.data;
      const items = Array.isArray(data) ? data : (data.questions || []);
      const count = Array.isArray(data) ? 0 : (data.answeredCount || 0);
      const paid = Array.isArray(data) ? false : !!data.hasPaidPlan1;
      setShowAnswer(Array(items.length).fill(false));
      setSelectedOptions([]);
      setQuestions(items);
      setAnsweredCount(count);
      setHasPaidPlan1(paid);
      setCurrentQuestionIndex(0);
      setPage(1);
      setHasMore(items.length === PAGE_SIZE);
      if (!paid && count >= FREE_QUESTIONS_LIMIT) {
        setShowTrialModal(true);
      } else {
        setShowTrialModal(false);
      }
    } catch {
      alert("Erro ao carregar as questões. Tente novamente mais tarde.");
    } finally {
      setLoadingQuestions(false);
    }
  }, [activeCourseId]);

  const loadMoreQuestions = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const body = selectedSubjectId ? { subjectId: Number(selectedSubjectId) } : {};
      if (selectedTopicId) body[activeCourseId ? 'topicId' : 'catalogTopicId'] = Number(selectedTopicId);
      body.page = nextPage;
      body.pageSize = PAGE_SIZE;
      body.random = true;
      body.courseId = activeCourseId || undefined;
      const response = await axios.post(apiUrl + "freeQuestions/", body);
      const data = response.data;
      const items = Array.isArray(data) ? data : (data.questions || []);
      const existingIds = new Set(questions.map((q) => q.id));
      const newItems = items.filter((q) => !existingIds.has(q.id));

      setQuestions((prev) => [...prev, ...newItems]);
      setShowAnswer((prev) => [...prev, ...Array(newItems.length).fill(false)]);
      setPage(nextPage);
      setHasMore(items.length === PAGE_SIZE && newItems.length > 0);
    } catch {
      alert("Erro ao carregar mais questões. Tente novamente mais tarde.");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const fetchSubjects = async () => {
      setLoadingSubjects(true);
      try {
        const response = await axios.get(apiUrl + "subjects", {
          params: { scope: 'free', courseId: activeCourseId || undefined }
        });
        setSubjects(response.data.subjects);
      } catch {
        alert("Erro ao carregar as matérias. Tente novamente mais tarde.");
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
    fetchQuestions(initialSubjectId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchQuestions, activeCourseId]);

  useEffect(() => {
    if (!selectedSubjectId) {
      setTopics([]);
      return;
    }
    const fetchTopics = async () => {
      setLoadingTopics(true);
      try {
        // Com concurso ativo, restringe aos assuntos cadastrados naquele
        // concurso+matéria específicos (edital real) — evita listar assuntos
        // de outros concursos que nem caem na prova selecionada. Sem concurso
        // ativo (ex.: usuário ainda sem plano), cai no fallback antigo, que
        // lista assuntos por matéria em qualquer concurso.
        const response = activeCourseId
          ? await axios.get(`${apiUrl}topics`, {
              params: { courseId: activeCourseId, subjectId: selectedSubjectId, onlyWithQuestions: true }
            })
          : await axios.get(`${apiUrl}topics/bySubject/${selectedSubjectId}`);
        setTopics(response.data.topics);
      } catch {
        setTopics([]);
      } finally {
        setLoadingTopics(false);
      }
    };
    fetchTopics();
  }, [selectedSubjectId, activeCourseId]);

  useEffect(() => {
    if (showTrialModal) {
      trackEvent('trial_modal_shown');
    }
  }, [showTrialModal]);

  const handleSubjectChange = (subjectId) => {
    setSelectedSubjectId(subjectId);
    setSelectedTopicId('');
    fetchQuestions(subjectId || null);
  };

  const handleTopicChange = (topicId) => {
    setSelectedTopicId(topicId);
    fetchQuestions(selectedSubjectId || null, topicId || null);
  };

  const sendAnwsers = async (option, questionIndex) => {
    if (option === undefined) {
      alert("Selecione uma alternativa antes de responder.");
      return;
    }

    let array = ["A", "B", "C", "D", "E"];

    let data = {
      "questionId": questions[questionIndex].id,
      "optionSelected": array[option],
      "courseSubjectId": questions[questionIndex].courseSubjectId || undefined,
      "courseId": activeCourseId || undefined
    };
    const updatedShowAnswer = [...showAnswer];
    updatedShowAnswer[questionIndex] = true;
    setShowAnswer(updatedShowAnswer);

    try {
      await axios.post(apiUrl + 'sendFreeAwnser', data);
      // Não recarregar as questões: o backend retorna apenas questões não respondidas,
      // então recarregar faria a questão responder sumir sem exibir o feedback.
      // Atualizamos localmente o contador e mantemos a pergunta visível com o feedback.
      setAnsweredCount((c) => {
        const newCount = (c || 0) + 1;
        if (!hasPaidPlan1 && newCount >= FREE_QUESTIONS_LIMIT) setShowTrialModal(true);
        return newCount;
      });
    } catch (error) {
      if (error.response?.status === 403 && error.response.data?.code === 'PLAN_LIMIT_REACHED') {
        setAnsweredCount(FREE_QUESTIONS_LIMIT);
        setShowTrialModal(true);
        return;
      }
      alert("Erro ao salvar sua resposta. Tente novamente.");
    }
  };

  const handleOptionChange = (questionIndex, option, optionIndex) => {
    setSelectedOptions({
      ...selectedOptions,
      [questionIndex]: optionIndex
    });

    const updatedShowAnswer = [...showAnswer];
    updatedShowAnswer[questionIndex] = false;
    setShowAnswer(updatedShowAnswer);
  };

  const getAnswers = (optionIndex) => {
    let array = ["A", "B", "C", "D", "E"];
    return array[optionIndex] + ")";
  };

  const selectedSubjectName = subjects.find(
    (subject) => String(subject.id) === String(selectedSubjectId)
  )?.name;

  // Teste grátis encerrado: o PlansModal já cobre a tela por cima, então o
  // progresso/dropdown de matéria (que só fazem sentido enquanto ainda dá pra
  // responder questões) somem — evita duplicar o convite de upgrade atrás do modal.
  const trialCompleted = !hasPaidPlan1 && answeredCount >= FREE_QUESTIONS_LIMIT;

  if (loadingSubjects) {
    return (
      <div className="free-questions-page">
        <DashboardTopbar label="Questões livres" />
        <div className="loading-message">Carregando matérias...</div>
      </div>
    );
  }

  return (
    <div className="free-questions-page free-questions-page--wide">

      {showTrialModal && (
        <PlansModal
          trackingPrefix="trial_modal"
          heroTitle="Teste grátis concluído"
          heroSubtitle="Continue praticando até a prova da Polícia Penal RS. Desbloqueie agora simulados, comentários e estatísticas completas."
          onClose={() => setShowTrialModal(false)}
        />
      )}

      <DashboardTopbar label="Questões livres" />

      <h2 className="free-questions-title">Pratique através de questões</h2>

      {!hasPaidPlan1 && !trialCompleted && (
        <div className="free-progress-banner-global">
          <div className="free-progress-banner-header">
            <span className="free-progress-info">
              Restam <strong>{FREE_QUESTIONS_LIMIT - answeredCount}</strong> questões grátis
            </span>
            <span className="free-progress-banner-count">{Math.min(answeredCount, FREE_QUESTIONS_LIMIT)}/{FREE_QUESTIONS_LIMIT}</span>
          </div>
          <div className="free-progress-banner-track">
            <div
              className="free-progress-banner-fill"
              style={{ width: `${Math.min((answeredCount / FREE_QUESTIONS_LIMIT) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}

      {!trialCompleted && (
        <div className="filtro-container">
          <span className="filtro-label">
            Selecione uma matéria específica:
          </span>
          <div className="filtro-dropdowns">
            <Dropdown
              value={selectedSubjectId}
              onChange={handleSubjectChange}
              options={[
                { value: '', label: 'Todas as matérias' },
                ...subjects.map((subject) => ({ value: String(subject.id), label: subject.name })),
              ]}
              headerLabel="Selecione uma matéria"
              placeholder="Todas as matérias"
              ariaLabel="Selecione uma matéria específica"
              className="filtro-dropdown dropdown--stretch"
            />

            {selectedSubjectId && !loadingTopics && topics.length > 0 && (
              <Dropdown
                value={selectedTopicId}
                onChange={handleTopicChange}
                options={[
                  { value: '', label: 'Todos os assuntos' },
                  ...topics.map((topic) => ({ value: String(topic.id), label: topic.name })),
                ]}
                headerLabel="Selecione um assunto"
                placeholder="Todos os assuntos"
                ariaLabel="Selecione um assunto específico"
                className="filtro-dropdown filtro-dropdown--assunto dropdown--stretch"
                searchable
                searchPlaceholder="Buscar assunto..."
              />
            )}
          </div>

          {selectedSubjectName && (
            <div className="filtro-selecionado">
              <p>Estudando: <strong>{selectedSubjectName}</strong></p>
            </div>
          )}
        </div>
      )}

      {loadingQuestions ? (
        <div className="loading-message">Carregando questões...</div>
      ) : trialCompleted ? null : questions.length === 0 ? (
        <div className="empty-message">
          Nenhuma questão encontrada para esta matéria, ou você já respondeu todas as questões disponíveis. Tente selecionar outra matéria ou volte mais tarde para novas questões!
        </div>
      ) : (
        <SingleFreeQuestionView
          questions={questions}
          currentQuestionIndex={currentQuestionIndex}
          setCurrentQuestionIndex={setCurrentQuestionIndex}
          selectedOptions={selectedOptions}
          onOptionChange={handleOptionChange}
          showAnswer={showAnswer}
          onAnswer={sendAnwsers}
          awnsers={awnsers}
          getAnswers={getAnswers}
          getQuestionImageSrc={getQuestionImageSrc}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMoreQuestions}
        />
      )}
    </div>
  );
}

export default FreeQuestions;
